import type { ReactNode } from "react";
import { RARITY_COLOR, RARITY_LABEL_FR, rarityFromOrdinal, type Rarity } from "@etheria/shared";
import { cn } from "../../lib/cn";

/**
 * Badge — pastille de statut. RarityBadge : pastille colorée par la rareté du jeu
 * (miroir de RARITY_COLOR dans @etheria/shared).
 */

export type BadgeTone =
  | "neutral"
  | "gold"
  | "arcane"
  | "violet"
  | "ember"
  | "blood"
  | "patina"
  | "success"
  | "muted";

const TONES: Record<BadgeTone, string> = {
  neutral: "border-bronze-500/40 bg-obsidian-100/70 text-parchment-200/85",
  gold: "border-gold-400/50 bg-gold-500/10 text-gold-200",
  arcane: "border-arcane-400/50 bg-arcane-500/12 text-arcane-200",
  violet: "border-violet-400/50 bg-violet-500/12 text-violet-200",
  ember: "border-ember-400/50 bg-ember-500/12 text-ember-200",
  blood: "border-blood-400/50 bg-blood-500/14 text-blood-100",
  patina: "border-patina-400/50 bg-patina-500/12 text-patina-200",
  success: "border-emerald-400/50 bg-emerald-500/12 text-emerald-200",
  muted: "border-white/10 bg-white/5 text-parchment-200/50",
};

interface BadgeProps {
  tone?: BadgeTone;
  className?: string;
  children: ReactNode;
  dot?: boolean;
  /** Style « sceau de cire » : pastille estampillée en relief plutôt que plate. */
  seal?: boolean;
}

const SEAL_TONES: Partial<Record<BadgeTone, string>> = {
  gold: "border-gold-500/60 bg-gold-500/15 text-gold-100 shadow-[inset_0_1px_0_rgba(246,226,168,0.18)]",
  blood: "border-blood-500/60 bg-blood-500/18 text-blood-100 shadow-[inset_0_1px_0_rgba(247,201,206,0.16)]",
  arcane: "border-arcane-500/60 bg-arcane-500/16 text-arcane-100 shadow-[inset_0_1px_0_rgba(176,236,226,0.16)]",
  violet: "border-violet-500/60 bg-violet-500/16 text-violet-100 shadow-[inset_0_1px_0_rgba(220,204,255,0.16)]",
};

export function Badge({ tone = "neutral", dot = false, seal = false, className, children }: BadgeProps) {
  const sealCls = seal ? SEAL_TONES[tone] : undefined;
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded border px-2.5 py-0.5 text-xs font-medium transition-colors duration-200",
        seal ? "font-display uppercase tracking-wider" : "",
        sealCls ?? TONES[tone],
        className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

interface RarityBadgeProps {
  /** rareté par libellé OU ordinal (0-6). */
  rarity: Rarity | number;
  className?: string;
  showLabel?: boolean;
}

/** Pastille de rareté colorée avec le hex exact du jeu. */
export function RarityBadge({ rarity, className, showLabel = true }: RarityBadgeProps) {
  const label: Rarity = typeof rarity === "number" ? rarityFromOrdinal(rarity) : rarity;
  const color = RARITY_COLOR[label];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded border px-2.5 py-0.5 text-xs font-semibold",
        className,
      )}
      style={{
        color,
        borderColor: `${color}66`,
        backgroundColor: `${color}1a`,
        boxShadow: `0 0 12px -4px ${color}59`,
        textShadow: `0 0 10px ${color}40`,
      }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {showLabel && RARITY_LABEL_FR[label]}
    </span>
  );
}
